/**
 * Shared "Back" navigation: return to the real previous in-app page when the
 * router has one, otherwise go to the page's parent (never a blanket /dashboard).
 */
import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/** True when the router history has an earlier entry in this tab (survives refresh). */
export function canGoBackInApp() {
  const st = window.history?.state;
  const idx = st && typeof st.idx === "number" ? st.idx : null;
  if (typeof idx === "number") return idx > 0;
  return (window.history?.length || 1) > 1;
}

const PAYROLL_PAGES = [
  "/payroll-process",
  "/pay-periods",
  "/process-payroll",
  "/payroll-runs",
  "/payroll-history",
  "/payroll-batch",
  "/payroll-finalize",
  "/payroll/arrears",
  "/payslip",
  "/payslips",
  "/salary-statement",
  "/tds-summary",
  "/summary",
];

const under = (path, base) => path === base || path.startsWith(`${base}/`);

/**
 * Parent page used when there is no previous in-app page (direct link, new tab).
 * Ordered most specific first.
 */
export function resolveBackFallback(pathname, search = "") {
  const path = ((pathname || "/").replace(/\/+$/, "") || "/").toLowerCase();
  const params = new URLSearchParams(search);

  if (under(path, "/dashboard")) return "/dashboard";

  // Settings area (settings workspace and the settings-sidebar pages) → All Settings.
  if (path === "/settings") return "/dashboard";
  if (
    under(path, "/settings") ||
    under(path, "/salary-v2") ||
    under(path, "/statutory") ||
    under(path, "/tools") ||
    path === "/payrollhome"
  ) {
    return "/settings";
  }

  // People
  if (path.startsWith("/employee-lifecycle/onboarding/")) return "/employee-lifecycle?tab=onboarding";
  if (path.startsWith("/employee-lifecycle/offboarding/")) return "/employee-lifecycle?tab=offboarding";
  if (path === "/employee-import/guide") return "/employees?view=import";
  if (
    under(path, "/employees") ||
    path.startsWith("/employeeedit/") ||
    ["/org-hierarchy", "/org-structure", "/employee-lifecycle", "/employeebulkupload", "/departments"].includes(path)
  ) {
    return path === "/employees" && !params.toString() ? "/dashboard" : "/employees";
  }

  // Attendance / Leave: sub pages and tabs go to the module dashboard.
  if (under(path, "/attendance")) return path === "/attendance" ? "/dashboard" : "/attendance";
  if (path === "/my-attendance") return "/dashboard";
  if (under(path, "/leave")) return path === "/leave" && params.get("tab") ? "/leave" : "/dashboard";
  if (path === "/holiday-calendar") return "/leave";

  // Payroll
  if (/^\/payroll\/[^/]+\/trace$/.test(path)) return "/payroll-history";
  if (PAYROLL_PAGES.some((p) => under(path, p))) {
    // Process Payroll tab of the hub steps back to the Payroll ID tab.
    if (path === "/payroll-process" && params.get("tab") === "process") return "/payroll-process?tab=periods";
    return "/payrollhome";
  }

  if (path === "/loans" && params.get("loan")) return "/loans";
  if (under(path, "/bank") && path !== "/bank") return "/bank";
  if (under(path, "/reports") && path !== "/reports") return "/reports";
  if (under(path, "/help") && path !== "/help") return "/help";
  if (under(path, "/support") && path !== "/support") return "/support";

  return "/dashboard";
}

/**
 * Returns a click handler for Back: history back when possible, else the parent page.
 * ``fallback`` overrides the computed parent page.
 */
export default function useSmartBack(fallback) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  return useCallback(() => {
    if (canGoBackInApp()) {
      navigate(-1);
      return;
    }
    navigate(fallback || resolveBackFallback(pathname, search), { replace: true });
  }, [navigate, fallback, pathname, search]);
}
